import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowLeftRight,
  Building2,
  CheckCircle2,
  Clock,
  Landmark,
  Users,
  Wallet,
  X,
} from 'lucide-react';
import { apiService } from '../../services/api.service';
import { useCashSession } from '../../components/CashSessionBar';
import { usePermissions } from '../../context/PermissionsContext';
import {
  formatMoney,
  registerTypeLabel,
  humanizeTreasuryError,
  CTA_PRIMARY,
} from './treasuryUi';
import { TreasurySkeleton } from './TreasuryFeedback';
import { useTenantStore } from '../../stores/tenant.store';

export type MoveKind = 'cashier' | 'structural' | 'deposit' | 'withdrawal';

type Step = 1 | 2 | 3 | 4 | 5;


const LINKABLE = new Set(['CENTRAL_TREASURY', 'BRANCH_MASTER']);

function humanizeTransferError(raw: unknown): string {
  return humanizeTreasuryError(raw, 'No pudimos mover los fondos.');
}

const KIND_OPTIONS: {
  id: MoveKind;
  title: string;
  description: string;
  icon: typeof Users;
  needsSession?: boolean;
  needsWrite?: boolean;
}[] = [
  {
    id: 'cashier',
    title: 'A otro cajero',
    description: 'Entre cajas operativas con sesiones abiertas.',
    icon: Users,
    needsSession: true,
  },
  {
    id: 'structural',
    title: 'Entre cajas de tesorería',
    description: 'Entre Central, Madre y Operativas.',
    icon: Landmark,
    needsWrite: true,
  },
  {
    id: 'deposit',
    title: 'Depositar efectivo en banco',
    description: 'Desde Central o Madre hacia una cuenta vinculada.',
    icon: Building2,
    needsWrite: true,
  },
  {
    id: 'withdrawal',
    title: 'Retirar efectivo del banco',
    description: 'Desde una cuenta bancaria hacia Tesorería Central.',
    icon: Wallet,
    needsWrite: true,
  },
];

export function MoveFundsWizard({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const legalEntityId = useTenantStore((s) => s.legalEntityId);
  const { can } = usePermissions();
  const canWrite = can('cash', 'write');
  const { session, refresh: refreshSession } = useCashSession();

  const preset = searchParams.get('type') as MoveKind | null;
  const presetBankId = searchParams.get('bank') || '';

  const [step, setStep] = useState<Step>(1);
  const [kind, setKind] = useState<MoveKind | null>(
    preset && ['cashier', 'structural', 'deposit', 'withdrawal'].includes(preset)
      ? preset
      : null,
  );

  const [registers, setRegisters] = useState<any[]>([]);
  const [openSessions, setOpenSessions] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [registerLinks, setRegisterLinks] = useState<Record<string, any[]>>({});
  const [bankBalances, setBankBalances] = useState<Record<string, number>>({});
  const [policyThreshold, setPolicyThreshold] = useState(100000);
  const [originBalance, setOriginBalance] = useState<number | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);

  const [fromRegisterId, setFromRegisterId] = useState('');
  const [toRegisterId, setToRegisterId] = useState('');
  const [toSessionId, setToSessionId] = useState('');
  const [bankAccountId, setBankAccountId] = useState(presetBankId);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any | null>(null);

  useEffect(() => {
    if (preset && ['cashier', 'structural', 'deposit', 'withdrawal'].includes(preset)) {
      setKind(preset);
      setStep(2);
    }
  }, [preset]);

  useEffect(() => {
    if (presetBankId) setBankAccountId(presetBankId);
  }, [presetBankId]);

  useEffect(() => {
    if (!legalEntityId && kind !== 'cashier') return;
    let cancelled = false;
    (async () => {
      setLoadingMeta(true);
      try {
        const tasks: Promise<any>[] = [
          apiService.getCashRegisters(undefined, legalEntityId || undefined),
          apiService.listOpenCashSessions(),
        ];
        if (canWrite && legalEntityId) {
          tasks.push(apiService.getTreasuryPolicy(legalEntityId));
          tasks.push(apiService.listBankAccounts());
          tasks.push(apiService.getTreasurySummary(legalEntityId).catch(() => null));
        }
        const [regs, opens, ...rest] = await Promise.all(tasks);
        if (cancelled) return;
        setRegisters(regs ?? []);
        setOpenSessions(opens ?? []);
        if (canWrite && legalEntityId) {
          const pol = rest[0];
          const accs = rest[1] ?? [];
          const summary = rest[2];
          setPolicyThreshold(Number(pol?.transferApprovalThreshold ?? 100000));
          const forEntity = (accs as any[]).filter(
            (a) => !legalEntityId || a.legalEntityId === legalEntityId,
          );
          setBankAccounts(forEntity);
          const balMap: Record<string, number> = {};
          for (const b of summary?.banks ?? []) {
            balMap[b.id] = Number(b.balance ?? 0);
          }
          setBankBalances(balMap);

          const linkable = (regs ?? []).filter((r: any) => LINKABLE.has(r.type));
          const entries = await Promise.all(
            linkable.map(async (r: any) => {
              try {
                const links = await apiService.getRegisterBankAccounts(r.id);
                return [r.id, links] as const;
              } catch {
                return [r.id, []] as const;
              }
            }),
          );
          if (!cancelled) setRegisterLinks(Object.fromEntries(entries));
        }
      } catch (e: any) {
        if (!cancelled) setError(humanizeTransferError(e?.response?.data?.message || e.message));
      } finally {
        if (!cancelled) setLoadingMeta(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [legalEntityId, canWrite, kind]);

  // Origin balance for selected source
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOriginBalance(null);
      try {
        if (kind === 'cashier' && session?.id) {
          const bal = await apiService.getCashSessionBalance(session.id);
          if (!cancelled) setOriginBalance(Number(bal.systemBalance ?? session.systemBalance ?? 0));
          return;
        }
        if ((kind === 'structural' || kind === 'deposit') && fromRegisterId) {
          const bal = await apiService.getCashRegisterLedgerBalance(fromRegisterId);
          if (!cancelled) setOriginBalance(Number(bal.balance ?? 0));
          return;
        }
        if (kind === 'withdrawal' && bankAccountId) {
          if (!cancelled) setOriginBalance(bankBalances[bankAccountId] ?? null);
        }
      } catch {
        if (!cancelled) setOriginBalance(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [kind, session, fromRegisterId, bankAccountId, bankBalances]);

  const amountNum = parseFloat(amount);
  const amountValid = !Number.isNaN(amountNum) && amountNum > 0;
  const willNeedApproval = amountValid && amountNum > policyThreshold;

  const estimatedAfter =
    originBalance != null && amountValid
      ? Math.round((originBalance - amountNum) * 100) / 100
      : null;

  const insufficient =
    originBalance != null && amountValid && amountNum > originBalance + 1e-9;

  const otherSessions = useMemo(
    () => openSessions.filter((s) => s.id !== session?.id && s.status !== 'CLOSED'),
    [openSessions, session?.id],
  );

  const depositRegisters = useMemo(
    () => registers.filter((r) => LINKABLE.has(r.type) && r.isActive !== false),
    [registers],
  );
  const withdrawalRegisters = useMemo(
    () => registers.filter((r) => r.type === 'CENTRAL_TREASURY' && r.isActive !== false),
    [registers],
  );

  const linkedAccountsForRegister = useMemo(() => {
    const links = registerLinks[fromRegisterId] ?? [];
    const ids = new Set(links.map((l: any) => l.bankAccountId));
    return bankAccounts.filter((a) => ids.has(a.id));
  }, [registerLinks, fromRegisterId, bankAccounts]);

  const linkedAccountsForCentral = useMemo(() => {
    const centralId = fromRegisterId || withdrawalRegisters[0]?.id;
    const links = registerLinks[centralId] ?? [];
    const ids = new Set(links.map((l: any) => l.bankAccountId));
    return bankAccounts.filter((a) => ids.has(a.id));
  }, [registerLinks, fromRegisterId, withdrawalRegisters, bankAccounts]);

  const originLabel = useMemo(() => {
    if (kind === 'cashier') {
      return session
        ? `${session.cashRegister?.branch?.name ?? '—'} · CAJA ${session.cashRegister?.number}`
        : '—';
    }
    if (kind === 'withdrawal') {
      const a = bankAccounts.find((x) => x.id === bankAccountId);
      return a ? `${a.bankName} ${a.alias || a.accountNumber || ''}`.trim() : '—';
    }
    const r = registers.find((x) => x.id === fromRegisterId);
    return r ? `${registerTypeLabel(r.type)} · ${r.name || r.code}` : '—';
  }, [kind, session, bankAccounts, bankAccountId, registers, fromRegisterId]);

  const destLabel = useMemo(() => {
    if (kind === 'cashier') {
      const s = otherSessions.find((x) => x.id === toSessionId);
      return s
        ? `${s.cashRegister?.branch?.name} · CAJA ${s.cashRegister?.number} — ${s.user?.firstName ?? ''}`
        : '—';
    }
    if (kind === 'deposit') {
      const a = bankAccounts.find((x) => x.id === bankAccountId);
      return a ? `${a.bankName} ${a.alias || a.accountNumber || ''}`.trim() : '—';
    }
    if (kind === 'withdrawal') {
      const central = registers.find((x) => x.id === toRegisterId) || withdrawalRegisters[0];
      return central ? `${central.type} · ${central.name || central.code}` : '—';
    }
    const r = registers.find((x) => x.id === toRegisterId);
    return r ? `${registerTypeLabel(r.type)} · ${r.name || r.code}` : '—';
  }, [
    kind,
    otherSessions,
    toSessionId,
    bankAccounts,
    bankAccountId,
    registers,
    toRegisterId,
    fromRegisterId,
    withdrawalRegisters,
  ]);

  const visibleKinds = KIND_OPTIONS.filter((o) => {
    if (o.needsWrite && !canWrite) return false;
    if (o.id === 'cashier' && !session && !canWrite) {
      // still show so user sees they need to open caja
      return true;
    }
    return true;
  });

  const canProceedStep2 = (): boolean => {
    if (!kind) return false;
    if (kind === 'cashier') return !!session?.id && !!toSessionId;
    if (kind === 'structural') return !!fromRegisterId && !!toRegisterId && fromRegisterId !== toRegisterId;
    if (kind === 'deposit') return !!fromRegisterId && !!bankAccountId;
    if (kind === 'withdrawal') {
      const central = toRegisterId || withdrawalRegisters[0]?.id;
      return !!bankAccountId && !!central;
    }
    return false;
  };

  const goClose = () => {
    if (embedded) return;
    if (window.history.length > 1) navigate(-1);
    else navigate(kind === 'cashier' ? '/cash' : '/treasury/transfers');
  };

  const submit = async () => {
    if (submitting || insufficient || !amountValid) return;
    setSubmitting(true);
    setError('');
    try {
      let res: any;
      if (kind === 'cashier') {
        res = await apiService.transferCash({
          toSessionId,
          amount: amountNum,
          notes: notes || undefined,
        });
        await refreshSession();
      } else if (kind === 'structural') {
        res = await apiService.requestTreasuryTransfer({
          fromRegisterId,
          toRegisterId,
          amount: amountNum,
          notes: notes || undefined,
        });
      } else if (kind === 'deposit') {
        res = await apiService.requestCashBankTransfer({
          direction: 'DEPOSIT',
          cashRegisterId: fromRegisterId,
          bankAccountId,
          amount: amountNum,
          notes: notes || undefined,
        });
      } else if (kind === 'withdrawal') {
        const centralId = toRegisterId || withdrawalRegisters[0]?.id;
        res = await apiService.requestCashBankTransfer({
          direction: 'WITHDRAWAL',
          cashRegisterId: centralId,
          bankAccountId,
          amount: amountNum,
          notes: notes || undefined,
        });
      }
      setResult(res);
      setStep(5);
    } catch (e: any) {
      setError(humanizeTransferError(e?.response?.data?.message || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  const pending = result?.status === 'PENDING';
  const executed = result?.status === 'COMPLETED';

  const shellClass = embedded
    ? 'space-y-4'
    : 'max-w-2xl mx-auto space-y-4';

  return (
    <div className={shellClass}>
      {!embedded && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Tesorería</p>
            <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5" /> Mover fondos
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Registrá depósitos y extracciones, o mové fondos entre cajas.
            </p>
          </div>
          <button
            type="button"
            onClick={goClose}
            className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
            aria-label="Cerrar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Steps indicator */}
      {step < 5 && (
        <ol className="flex flex-wrap gap-2 text-xs">
          {[
            [1, 'Tipo'],
            [2, 'Origen / destino'],
            [3, 'Monto'],
            [4, 'Revisión'],
          ].map(([n, label]) => (
            <li
              key={n}
              className={`px-2.5 py-1 rounded-full ${
                step === n
                  ? 'bg-slate-900 text-white'
                  : step > (n as number)
                    ? 'bg-slate-200 text-slate-700'
                    : 'bg-slate-100 text-slate-400'
              }`}
            >
              {n}. {label}
            </li>
          ))}
        </ol>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {loadingMeta && step > 1 && step < 5 && <TreasurySkeleton kind="wizard" />}

      {/* STEP 1 */}
      {step === 1 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-900">¿A dónde querés mover el dinero?</h2>
          <div className="grid gap-2">
            {visibleKinds.map((o) => {
              const Icon = o.icon;
              const disabled = o.needsSession && !session;
              return (
                <button
                  key={o.id}
                  type="button"
                  disabled={disabled && o.id === 'cashier' && !session}
                  onClick={() => {
                    if (o.id === 'cashier' && !session) {
                      setError('Abrí tu caja operativa antes de transferir a otro cajero.');
                      return;
                    }
                    setError('');
                    setKind(o.id);
                    setFromRegisterId('');
                    setToRegisterId('');
                    setToSessionId('');
                    setBankAccountId('');
                    setStep(2);
                  }}
                  className={`text-left flex gap-3 rounded-lg border bg-white p-4 hover:border-slate-400 transition ${
                    disabled && o.id === 'cashier' ? 'opacity-60' : ''
                  }`}
                >
                  <Icon className="h-5 w-5 text-slate-600 shrink-0 mt-0.5" />
                  <span>
                    <span className="block font-medium text-slate-900">{o.title}</span>
                    <span className="block text-sm text-slate-500 mt-0.5">{o.description}</span>
                    {o.id === 'cashier' && !session && (
                      <span className="block text-xs text-amber-800 mt-1">
                        Necesitás una sesión abierta.{' '}
                        <Link to="/cash?open=1" className="underline">
                          Abrir caja
                        </Link>
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* STEP 2 */}
      {step === 2 && kind && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Origen y destino</h2>

          {kind === 'cashier' && (
            <div className="space-y-3">
              <div className="rounded-md border bg-slate-50 px-3 py-2 text-sm">
                <p className="text-slate-500 text-xs">Origen (tu sesión)</p>
                <p className="font-medium">{originLabel}</p>
              </div>
              <label className="block text-sm">
                <span className="font-medium">Cajero destino</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={toSessionId}
                  onChange={(e) => setToSessionId(e.target.value)}
                >
                  <option value="">Seleccioná…</option>
                  {otherSessions.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.cashRegister?.branch?.name} · CAJA {s.cashRegister?.number} —{' '}
                      {s.user?.firstName}
                    </option>
                  ))}
                </select>
              </label>
              {otherSessions.length === 0 && (
                <p className="text-sm text-amber-800">No hay otras sesiones abiertas ahora.</p>
              )}
            </div>
          )}

          {kind === 'structural' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-medium">Origen</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={fromRegisterId}
                  onChange={(e) => setFromRegisterId(e.target.value)}
                >
                  <option value="">Seleccioná…</option>
                  {registers.map((r) => (
                    <option key={r.id} value={r.id}>
                      {registerTypeLabel(r.type)} · {r.name || r.code}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium">Destino</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={toRegisterId}
                  onChange={(e) => setToRegisterId(e.target.value)}
                >
                  <option value="">Seleccioná…</option>
                  {registers
                    .filter((r) => r.id !== fromRegisterId)
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {registerTypeLabel(r.type)} · {r.name || r.code}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          )}

          {kind === 'deposit' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-medium">Origen (Central / Madre)</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={fromRegisterId}
                  onChange={(e) => {
                    setFromRegisterId(e.target.value);
                    setBankAccountId('');
                  }}
                >
                  <option value="">Seleccioná…</option>
                  {depositRegisters.map((r) => (
                    <option key={r.id} value={r.id}>
                      {registerTypeLabel(r.type)} · {r.name || r.code}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium">Cuenta bancaria vinculada</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  disabled={!fromRegisterId}
                >
                  <option value="">Seleccioná…</option>
                  {linkedAccountsForRegister.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.bankName} {a.alias || a.accountNumber || ''}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}

          {kind === 'withdrawal' && (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="font-medium">Cuenta bancaria</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                >
                  <option value="">Seleccioná…</option>
                  {linkedAccountsForCentral.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.bankName} {a.alias || a.accountNumber || ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="font-medium">Destino (Central)</span>
                <select
                  className="mt-1 w-full border rounded-md px-3 py-2"
                  value={toRegisterId || withdrawalRegisters[0]?.id || ''}
                  onChange={(e) => {
                    setToRegisterId(e.target.value);
                    setFromRegisterId(e.target.value);
                  }}
                >
                  {withdrawalRegisters.map((r) => (
                    <option key={r.id} value={r.id}>
                      {registerTypeLabel(r.type)} · {r.name || r.code}
                    </option>
                  ))}
                </select>
              </label>
              {withdrawalRegisters.length === 0 && (
                <p className="sm:col-span-2 text-sm text-amber-800">
                  No hay Tesorería Central activa para esta empresa.
                </p>
              )}
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border hover:bg-slate-50"
              onClick={() => {
                setStep(1);
                setError('');
              }}
            >
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
            <button
              type="button"
              disabled={!canProceedStep2()}
              className={CTA_PRIMARY}
              onClick={() => setStep(3)}
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {step === 3 && kind && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Monto</h2>
          <div className="rounded-lg border bg-white p-4 space-y-2 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-slate-500">Saldo origen</span>
              <span className="font-medium tabular-nums">
                {originBalance == null ? '…' : formatMoney(originBalance)}
              </span>
            </div>
            <label className="block">
              <span className="text-slate-500">Monto a mover</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                className="mt-1 w-full border rounded-md px-3 py-2 text-base font-medium"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </label>
            <div className="flex justify-between gap-2 border-t pt-2">
              <span className="text-slate-500">Saldo estimado después</span>
              <span
                className={`font-semibold tabular-nums ${
                  insufficient ? 'text-red-700' : 'text-slate-900'
                }`}
              >
                {estimatedAfter == null ? '—' : formatMoney(estimatedAfter)}
              </span>
            </div>
            {insufficient && (
              <p className="text-sm text-red-700">No hay saldo suficiente.</p>
            )}
          </div>
          <label className="block text-sm">
            <span className="text-slate-600">Observaciones (opcional)</span>
            <input
              className="mt-1 w-full border rounded-md px-3 py-2"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
          <div className="flex justify-between gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border hover:bg-slate-50"
              onClick={() => setStep(2)}
            >
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
            <button
              type="button"
              disabled={!amountValid || insufficient}
              className={CTA_PRIMARY}
              onClick={() => setStep(4)}
            >
              Continuar
            </button>
          </div>
        </div>
      )}

      {/* STEP 4 */}
      {step === 4 && kind && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-900">Revisión</h2>
          <dl className="rounded-lg border bg-white divide-y text-sm">
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Tipo</dt>
              <dd className="font-medium text-right">
                {KIND_OPTIONS.find((k) => k.id === kind)?.title}
              </dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Origen</dt>
              <dd className="font-medium text-right">{originLabel}</dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Destino</dt>
              <dd className="font-medium text-right">{destLabel}</dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Monto</dt>
              <dd className="font-semibold tabular-nums">{formatMoney(amountNum)}</dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Saldo origen → después</dt>
              <dd className="tabular-nums text-right">
                {originBalance == null ? '—' : formatMoney(originBalance)} →{' '}
                {estimatedAfter == null ? '—' : formatMoney(estimatedAfter)}
              </dd>
            </div>
          </dl>

          {willNeedApproval && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950 space-y-1">
              <p className="font-semibold">Esta transferencia requiere aprobación.</p>
              <p>
                Al confirmar quedará pendiente hasta que un usuario autorizado la apruebe.
              </p>
              <p className="text-xs text-amber-800">
                Umbral actual: {formatMoney(policyThreshold)}
              </p>
            </div>
          )}

          <div className="flex justify-between gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md border hover:bg-slate-50"
              onClick={() => setStep(3)}
              disabled={submitting}
            >
              <ArrowLeft className="h-4 w-4" /> Atrás
            </button>
            <button
              type="button"
              disabled={submitting || insufficient}
              className={CTA_PRIMARY}
              onClick={() => void submit()}
            >
              {submitting ? 'Procesando…' : willNeedApproval ? 'Solicitar transferencia' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}

      {/* STEP 5 */}
      {step === 5 && result && (
        <div className="space-y-4">
          {executed ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-6 text-center space-y-2">
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" />
              <p className="text-lg font-semibold text-emerald-950">Fondos transferidos</p>
            </div>
          ) : (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-6 text-center space-y-2">
              <Clock className="h-10 w-10 text-amber-700 mx-auto" />
              <p className="text-lg font-semibold text-amber-950">
                Transferencia pendiente de aprobación
              </p>
              <p className="text-sm text-amber-900">Estado: PENDIENTE DE APROBACIÓN</p>
            </div>
          )}

          <dl className="rounded-lg border bg-white divide-y text-sm">
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Origen</dt>
              <dd className="font-medium text-right">{originLabel}</dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Destino</dt>
              <dd className="font-medium text-right">{destLabel}</dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Monto</dt>
              <dd className="font-semibold tabular-nums">
                {formatMoney(Number(result.amount ?? amountNum))}
              </dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">Fecha</dt>
              <dd>
                {new Date(result.createdAt || result.requestedAt || Date.now()).toLocaleString(
                  'es-AR',
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-3 px-4 py-3">
              <dt className="text-slate-500">ID operación</dt>
              <dd className="font-mono text-xs break-all text-right">{result.id}</dd>
            </div>
          </dl>

          <div className="flex flex-wrap gap-2 justify-end">
            <Link
              to="/treasury/transfers"
              className="px-3 py-2 text-sm rounded-md border hover:bg-slate-50"
            >
              Ver movimientos de fondos
            </Link>
            {(pending || !executed) && (
              <Link
                to="/treasury/approvals"
                className="px-3 py-2 text-sm rounded-md border hover:bg-slate-50"
              >
                Ir a aprobaciones
              </Link>
            )}
            <button
              type="button"
              className={CTA_PRIMARY}
              onClick={() => {
                setResult(null);
                setAmount('');
                setNotes('');
                setStep(1);
                setKind(null);
                setError('');
              }}
            >
              Nueva operación
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
